import {
  booleanAttribute,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormControl,
  FormGroupDirective,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { DateAdapter, ErrorStateMatcher, MAT_DATE_FORMATS } from '@angular/material/core';
import { DATE_TIME_FIELD_FORMATS, DateTimeFieldDateAdapter } from '@digilize/shared/ui/material/src';

export interface DateTimeFieldChange {
  value: Date | null;
}

/**
 * One date-and-time value edited as a Material date field and a time field side by side. The calendar only picks a
 * day, so the component keeps the time itself: picking a day keeps the time already set (or `defaultTime` for a new
 * value), and the time field only changes the time. The time field stays disabled until there is a day.
 *
 * Errors go in as content (`<mat-error>` or `.mat-error`) and show under the date field; a floating label goes in
 * as `label`, because a form field only sees the `<mat-label>` and `<mat-error>` declared in its own template.
 */
@Component({
  selector: 'lib-date-time-field',
  templateUrl: './date-time-field.component.html',
  styleUrls: ['./date-time-field.component.scss'],
  providers: [
    { provide: DateAdapter, useClass: DateTimeFieldDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: DATE_TIME_FIELD_FORMATS },
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DateTimeFieldComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => DateTimeFieldComponent), multi: true },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DateTimeFieldComponent implements ControlValueAccessor, Validator, OnInit, OnChanges {
  @Input() label?: string;
  /** Earliest allowed date and time; an earlier value sets the `dateTimeMin` error. */
  @Input() min?: Date | string | number | null;
  /** Minutes between the times offered in the list. Any minute can still be typed. */
  @Input() stepMinute?: number;
  /** Time a value gets when its day is picked first, as [hours, minutes, seconds]. */
  @Input() defaultTime: [number, number, number] = [0, 0, 0];
  @Input({ transform: booleanAttribute }) touchUi = false;
  /** No typing; the pickers still open. */
  @Input({ transform: booleanAttribute }) readonly = false;
  @Output() dateChange = new EventEmitter<DateTimeFieldChange>();

  readonly dateCtrl = new FormControl<Date | null>(null);
  readonly timeCtrl = new FormControl<Date | null>({ value: null, disabled: true });

  // Shows both fields red when the bound control is invalid, as the single ngx field did.
  readonly errorStateMatcher: ErrorStateMatcher = {
    isErrorState: () =>
      (!!this.outer?.control?.invalid && (this.outer.control.touched || !!this.parentForm?.submitted)) ||
      (this.dateCtrl.invalid && this.dateCtrl.dirty),
  };

  private readonly adapter = inject<DateAdapter<Date>>(DateAdapter);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly parentForm = inject(FormGroupDirective, { optional: true });
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private outer: NgControl | null = null;
  private value: Date | null = null;
  private disabled = false;
  private onChange: (value: Date | null) => void = () => undefined;
  private onValidatorChange: () => void = () => undefined;
  onTouched: () => void = () => undefined;

  get minDate(): Date | null {
    if (this.min === null || this.min === undefined || this.min === '') return null;
    const date = new Date(this.min as Date);
    return isNaN(date.getTime()) ? null : date;
  }

  // The global `.form-field.readonly` style greys a field out; a `readonly` class on this element passes it on.
  get greyedOut(): boolean {
    return this.host.nativeElement.classList.contains('readonly');
  }

  get interval(): string {
    return `${Math.max(this.stepMinute || 15, 5)}m`;
  }

  ngOnInit(): void {
    // Looked up here, not injected: NgControl depends on this value accessor. Its `control` is only set up after this
    // hook (the forms directive's own hooks run later), so it is read when needed.
    this.outer = this.injector.get(NgControl, null, { self: true, optional: true });
    this.dateCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((day) => this.onDayPicked(day));
    this.timeCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((time) => this.onTimePicked(time));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['min']) this.onValidatorChange();
  }

  writeValue(value: unknown): void {
    const date = value instanceof Date ? value : value ? this.adapter.deserialize(value) : null;
    this.value = date && this.adapter.isValid(date) ? date : null;
    this.dateCtrl.setValue(this.value, { emitEvent: false });
    this.timeCtrl.setValue(this.value, { emitEvent: false });
    this.updateTimeEnabled();
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) this.dateCtrl.disable({ emitEvent: false });
    else this.dateCtrl.enable({ emitEvent: false });
    this.updateTimeEnabled();
  }

  validate(): ValidationErrors | null {
    const min = this.minDate;
    return this.value && min && this.value < min ? { dateTimeMin: { min, actual: this.value } } : null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  private onDayPicked(day: Date | null): void {
    if (!day || !this.adapter.isValid(day)) {
      this.commit(null);
      return;
    }
    const [hours, minutes, seconds] = this.value
      ? [this.adapter.getHours(this.value), this.adapter.getMinutes(this.value), this.adapter.getSeconds(this.value)]
      : this.defaultTime;
    this.commit(this.adapter.setTime(day, hours, minutes, seconds));
  }

  private onTimePicked(time: Date | null): void {
    if (!time || !this.adapter.isValid(time) || !this.value) return;
    const value = this.adapter.setTime(
      this.value,
      this.adapter.getHours(time),
      this.adapter.getMinutes(time),
      this.adapter.getSeconds(time)
    );
    if (value.getTime() !== this.value.getTime()) this.commit(value);
  }

  private commit(value: Date | null): void {
    this.value = value;
    this.timeCtrl.setValue(value, { emitEvent: false });
    this.updateTimeEnabled();
    this.onChange(value);
    this.dateChange.emit({ value });
  }

  private updateTimeEnabled(): void {
    if (this.disabled || !this.value) this.timeCtrl.disable({ emitEvent: false });
    else this.timeCtrl.enable({ emitEvent: false });
  }
}
